import "./App.css";
import About from "./About";
import Nav from "./Nav";
import { useEffect } from "react";
import Projects from "./Projects";
import Education from "./Education";
import ScrollToTop from "react-scroll-to-top";
import ReactGA from "react-ga4";

const TRACKING_ID = "G-7PCKTLSRWH";

const heroChips = [
  "Customer-facing delivery",
  "SDK/API implementation",
  "REST API development",
  "B2B SaaS experience",
  "Cloud & data automation",
  "Engineering escalation reduction",
  "Australia + APAC experience",
  "AWS Solutions Architect Associate",
];

const backgroundGroups = [
  {
    category: "Work Experience",
    items: [
      {
        label: "Social Plus",
        title: "Solution Engineer (APAC market)",
        context: "APAC remote experience",
        image: "./social+.jpeg",
        imageAlt: "Social Plus logo",
        detail:
          "Current solution engineering work across enterprise SaaS, SDKs, APIs, demos, RFPs, implementation paths, and product troubleshooting.",
      },
      {
        label: "RMIT University",
        title: "Information Technology Advisor - RMIT University (Melbourne)",
        context: "Australia work experience",
        image: "./rmit.png",
        imageAlt: "RMIT University logo",
        detail:
          "Provided ITIL-based support for staff, teachers, and students using ServiceNow, incident triage, SLA management, IAM, Azure, Active Directory, Intune, and SCCM.",
      },
      {
        label: "Insider",
        title: "Senior Technical Support Consultant - Insider (APAC market)",
        context: "APAC remote experience",
        image: "./insider.jpeg",
        imageAlt: "Insider logo",
        detail:
          "Resolved 1,100+ tickets, supported SDK implementations, handled web and mobile integrations, and kept communication clear under APAC production pressure.",
      },
    ],
  },
  {
    category: "Internship",
    items: [
      {
        label: "Shine Solutions Group",
        title: "Software Engineer Intern - Shine Solutions Group",
        context: "Australia work experience",
        image: "./shine.png",
        imageAlt: "Shine Solutions Group logo",
        detail:
          "Melbourne internship building AWS serverless applications, CI/CD workflows, automated tests, and infrastructure templates with engineering teams.",
      },
    ],
  },
  {
    category: "Certification & Education",
    items: [
            {
        label: "RMIT University",
        title: "Master of Information Technology",
        context: "Australia education",
        image: "./rmit.png",
        imageAlt: "RMIT University logo",
        detail:
          "GPA 3.8, international student scholarship recipient, Student Mentor, and Australian study experience in Melbourne.",
      },
      {
        label: "AWS",
        title: "AWS Certified Solutions Architect - Associate",
        detail:
          "Cloud architecture foundation backed by hands-on serverless work with Lambda, API Gateway, S3, Step Functions, Bedrock, and CloudFormation.",
        image: "./aws.png",
        imageAlt: "AWS logo",
      }
    ],
  },
];

const metrics = [
  {
    value: "50%",
    label: "fewer engineering escalations",
    context: "through better triage, reproduction quality, and direct fixes",
  },
  {
    value: "1,100+",
    label: "support tickets resolved",
    context: "across APAC B2B customer environments",
  },
  {
    value: "99.1%",
    label: "customer satisfaction",
    context: "from technical support and troubleshooting delivery",
  },
  {
    value: "20+",
    label: "successful integrations",
    context: "across web and mobile implementation work",
  },
  {
    value: "100%",
    label: "client retention",
    context: "while managing customer onboarding and product guidance",
  },
  {
    value: "Days to minutes",
    label: "documentation workflow",
    context: "standardized solution design documents and references",
  },
];

const fitCards = [
  {
    title: "Customer-facing",
    text: "RFPs, demos, technical discovery, and pre/post-sales support for enterprise customers.",
  },
  {
    title: "Technical depth",
    text: "Hands-on experience across REST APIs, SDKs, service integrations, cloud services, debugging, and code-level product fixes.",
  },
  {
    title: "Business impact",
    text: "Reduced engineering escalations, automated manual workflows, and improved customer-facing delivery.",
  },
];

const effectivenessCards = [
  {
    title: "Product depth",
    text: "I learn products deeply enough to explain, debug, and recommend the right implementation path.",
  },
  {
    title: "Customer communication",
    text: "I turn unclear customer requirements into structured technical discussions and next steps.",
  },
  {
    title: "Engineering communication",
    text: "I provide clear reproduction steps, code-level findings, and actionable context for engineering teams.",
  },
  {
    title: "Solution ownership",
    text: "I follow through from discovery and design to demo, troubleshooting, documentation, and delivery.",
  },
  {
    title: "Practical delivery",
    text: "I build POCs, answer RFPs, write solution designs, and avoid overpromising.",
  },
];

const skillGroups = [
  {
    title: "Solution Engineering",
    skills: [
      "RFPs",
      "Demos",
      "Technical discovery",
      "Solution design",
      "B2B SaaS",
      "API/SDK recommendations",
      "Pre-sales",
      "Post-sales",
      "Customer troubleshooting",
      "Enterprise implementation",
    ],
  },
  {
    title: "Implementation & Debugging",
    skills: [
      "APIs",
      "REST API development",
      "SDKs",
      "Service integrations",
      "Debugging",
      "Code-level investigation",
      "POCs",
      "PR fixes",
      "Integration support",
      "Solution documentation",
    ],
  },
  {
    title: "Cloud & Data Platforms",
    skills: [
      "AWS",
      "GCP BigQuery",
      "OpenSearch",
      "Athena",
      "Looker Studio",
      "Lambda",
      "S3",
      "API Gateway",
      "Step Functions",
      "Bedrock",
      "Rekognition",
      "CloudFormation",
      "SAM",
    ],
  },
  {
    title: "Frontend & Mobile",
    skills: [
      "React",
      "TypeScript",
      "JavaScript",
      "HTML",
      "CSS",
      "Flutter",
      "iOS",
      "Android",
      "SDK customization",
      "UIKit fixes",
    ],
  },
  {
    title: "Enterprise IT",
    skills: [
      "ServiceNow",
      "ITIL",
      "Azure",
      "Active Directory",
      "IAM",
      "Intune",
      "SCCM",
      "SLA management",
    ],
  },
  {
    title: "Cross-Functional Delivery",
    skills: [
      "Jira",
      "Agile",
      "Documentation",
      "Product collaboration",
      "Engineering collaboration",
      "QA",
      "Customer success",
      "Enterprise customers",
    ],
  },
];

const SectionHeading = ({ eyebrow, title, children }) => (
  <div className="mb-6">
    <p className="text-xs font-bold uppercase tracking-[0.22em] text-sky-600">
      {eyebrow}
    </p>
    <h2 className="mt-2 text-2xl font-bold text-slate-950">{title}</h2>
    {children ? (
      <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600">
        {children}
      </p>
    ) : null}
  </div>
);

const Chip = ({ children }) => (
  <span className="rounded-full border border-slate-200 bg-white/75 px-3 py-1 text-xs font-medium text-slate-600">
    {children}
  </span>
);

const SkillChip = ({ children }) => (
  <span className="inline-flex min-h-8 items-center rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium leading-5 text-slate-700">
    {children}
  </span>
);

const Card = ({ title, children }) => (
  <article className="rounded-lg border border-slate-200 bg-white/70 p-5 shadow-sm">
    <h3 className="text-base font-semibold text-slate-950">{title}</h3>
    <p className="mt-2 text-sm leading-6 text-slate-600">{children}</p>
  </article>
);

const BackgroundHighlights = () => (
  <section id="background" className="pt-16">
    <SectionHeading eyebrow="Background" title="Where the proof comes from">
      A structured view of the credentials, internship, and work experience that
      shaped how I work with customers, engineering teams, and cloud products.
    </SectionHeading>

    <div className="space-y-6">
      {backgroundGroups.map((group) => (
        <div key={group.category}>
          <h3 className="mb-3 text-sm font-bold uppercase tracking-[0.2em] text-slate-500">
            {group.category}
          </h3>
          <div className="grid gap-4">
            {group.items.map((item) => (
              <article
                className="overflow-hidden rounded-lg border border-slate-200 bg-white/70 shadow-sm"
                key={item.title}
              >
                <div className="grid gap-0 sm:grid-cols-[150px_1fr]">
                  <div className="flex min-h-24 items-center justify-center border-b border-slate-200 bg-slate-50 p-4 sm:border-b-0 sm:border-r">
                    {item.image ? (
                      <img
                        className="h-full max-h-24 w-full rounded-md object-contain"
                        src={item.image}
                        alt={item.imageAlt}
                      />
                    ) : (
                      <div className="flex h-16 w-16 items-center justify-center rounded-md border border-slate-200 bg-white text-xs font-bold uppercase tracking-wide text-slate-700">
                        {item.label}
                      </div>
                    )}
                  </div>
                  <div className="p-5">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-sky-600">
                      {item.label}
                    </p>
                    <h4 className="mt-2 text-base font-semibold text-slate-950">
                      {item.title}
                    </h4>
                    {item.context ? (
                      <p className="mt-2 inline-flex rounded-md bg-slate-950 px-2.5 py-1 text-xs font-bold uppercase tracking-[0.16em] text-white">
                        {item.context}
                      </p>
                    ) : null}
                    <p className="mt-2 text-sm leading-6 text-slate-600">
                      {item.detail}
                    </p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      ))}
    </div>
  </section>
);

function App() {
  useEffect(() => {
    ReactGA.initialize(TRACKING_ID);
    ReactGA.send({ hitType: "pageview", page: "/", title: "Landing Page" });
  }, []);

  return (
    <>
      <div className="mx-auto min-h-screen max-w-6xl px-6 py-10 font-sans md:px-12 lg:px-16">
        <header className="lg:flex lg:items-start lg:justify-between lg:gap-12">
          <div className="lg:sticky lg:top-8 lg:w-[38%]">
            <img
              className="profile rounded-lg border border-slate-200 object-cover shadow-sm"
              src="./profile.png"
              alt="Chanakan Amornpatchara"
            />
            <Nav />
          </div>

          <main className="mt-10 lg:mt-0 lg:w-[62%]">
            <section id="about" className="pt-2">
              <p className="text-sm font-semibold uppercase tracking-[0.22em] text-sky-600">
                Solution Engineer
              </p>
              <h1 className="mt-4 text-4xl font-bold leading-tight text-slate-950 sm:text-5xl">
                Chanakan "Guy" Amornpatchara
              </h1>
              <p className="mt-5 max-w-3xl text-2xl font-semibold leading-snug text-slate-800">
                Solution Engineer turning enterprise requirements into scalable
                technical solutions.
              </p>
              <p className="mt-4 max-w-2xl text-base leading-7 text-slate-600">
                Customer-facing technical engineer with B2B SaaS, REST API,
                SDK, cloud, and enterprise service integration experience
                across APAC and Australia.
              </p>

              <div className="mt-6 flex flex-wrap gap-3">
                <a
                  className="inline-flex items-center rounded-md bg-slate-950 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
                  href="https://www.linkedin.com/in/cnkamorn"
                  target="_blank"
                  rel="noreferrer"
                >
                  LinkedIn
                </a>          
              </div>

              <div className="mt-6 flex flex-wrap gap-2">
                {heroChips.map((chip) => (
                  <Chip key={chip}>{chip}</Chip>
                ))}
              </div>

            </section>

            <About />
            <BackgroundHighlights />
            <Education />

            <section id="impact" className="pt-16">
              <SectionHeading eyebrow="Impact" title="Evidence at a glance">
                Compact proof points from customer-facing technical delivery,
                SaaS implementation, cloud engineering, and internal automation.
              </SectionHeading>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {metrics.map((metric) => (
                  <article
                    className="rounded-lg border border-slate-200 bg-white/70 p-4 shadow-sm"
                    key={metric.label}
                  >
                    <p className="text-2xl font-bold text-slate-950">
                      {metric.value}
                    </p>
                    <h3 className="mt-1 text-sm font-semibold text-slate-800">
                      {metric.label}
                    </h3>
                    <p className="mt-2 text-xs leading-5 text-slate-500">
                      {metric.context}
                    </p>
                  </article>
                ))}
              </div>
            </section>

            <section id="fit" className="pt-16">
              <SectionHeading
                eyebrow="Fit"
                title="Why I fit Solution Engineering"
              />
              <div className="grid gap-4 md:grid-cols-3">
                {fitCards.map((card) => (
                  <Card key={card.title} title={card.title}>
                    {card.text}
                  </Card>
                ))}
              </div>
            </section>

            <section id="effective" className="pt-16">
              <SectionHeading
                eyebrow="Operating Style"
                title="What makes me effective"
              />
              <div className="grid gap-4 sm:grid-cols-2">
                {effectivenessCards.map((card) => (
                  <Card key={card.title} title={card.title}>
                    {card.text}
                  </Card>
                ))}
              </div>
            </section>

            <Projects />

            <section id="skills" className="pt-16">
              <SectionHeading
                eyebrow="Skills"
                title="Technical & Solution Capabilities"
              >
                A grouped view of how I work across customer discovery,
                implementation, cloud platforms, and cross-functional delivery.
              </SectionHeading>
              <div className="grid gap-3 md:grid-cols-2">
                {skillGroups.map((group) => (
                  <article
                    className="rounded-lg border border-slate-200 border-l-sky-500 bg-white/80 p-4 shadow-sm ring-1 ring-transparent transition hover:border-slate-300 hover:bg-white hover:ring-slate-200"
                    key={group.title}
                  >
                    <div className="border-b border-slate-100 pb-3">
                      <h3 className="text-sm font-bold leading-5 text-slate-950">
                        {group.title}
                      </h3>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {group.skills.map((skill) => (
                        <SkillChip key={skill}>{skill}</SkillChip>
                      ))}
                    </div>
                  </article>
                ))}
              </div>
            </section>

            <section
              id="contact"
              className="mt-16 rounded-lg border border-slate-200 bg-white/70 p-6 shadow-sm"
            >
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-sky-600">
                Contact
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <a
                  className="inline-flex items-center rounded-md bg-slate-950 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
                  href="https://www.linkedin.com/in/cnkamorn"
                  target="_blank"
                  rel="noreferrer"
                >
                  Contact on LinkedIn
                </a>
              
              </div>
            </section>
          </main>
        </header>

        <ScrollToTop
          top={900}
          smooth
          component={
            <div>
              <svg
                width="38px"
                height="38px"
                viewBox="0 0 25 25"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8 14.5L12.5 10L17 14.5"
                  stroke="#121923"
                  strokeWidth="1.2"
                />
              </svg>
            </div>
          }
        />
      </div>
      <footer className="mx-auto max-w-6xl px-6 pb-8 text-sm text-slate-500 md:px-12 lg:px-16">
        © 2026 Chanakan Amornpatchara
      </footer>
    </>
  );
}

export default App;
